// Thin client for the /api/experiments endpoints served by the Cloudflare
// Worker (src/worker.js) over D1. An "experiment" is a named (seed, params,
// stats-snapshot) record, so a run started on one machine is reachable from any
// other on the same domain. In `vite dev` (no Worker) these calls simply fail
// and the UI reports it — run `wrangler dev` to exercise persistence.

import type { SimParams } from "../config";

export interface ExperimentSummary {
  id: string;
  name: string;
  seed: number;
  updated_at: string;
}

export interface ExperimentRecord extends ExperimentSummary {
  params: SimParams;
  stats: Record<string, number[]>;
}

export async function saveExperiment(
  name: string,
  seed: number,
  params: SimParams,
  stats: Record<string, number[]>,
): Promise<ExperimentSummary> {
  const res = await fetch("/api/experiment", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name, seed, params, stats }),
  });
  if (!res.ok) throw new Error(`save failed: ${res.status}`);
  return res.json();
}

export async function listExperiments(): Promise<ExperimentSummary[]> {
  const res = await fetch("/api/experiments");
  if (!res.ok) throw new Error(`list failed: ${res.status}`);
  return res.json();
}

export async function loadExperiment(id: string): Promise<ExperimentRecord> {
  const res = await fetch(`/api/experiments/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`load failed: ${res.status}`);
  return res.json();
}

// --- Picker ------------------------------------------------------------------
// A saved run is chosen from a real list, not from `window.prompt("1. …\n2. …")`
// asking for a number — which on a phone is a wall of text in a system dialog
// with a numeric guess as the answer. Self-contained: no dependencies, styles
// injected once, resolves null on cancel.

/** Show the saved runs and resolve with the chosen one, or null if cancelled. */
export function pickExperiment(items: ExperimentSummary[]): Promise<ExperimentSummary | null> {
  injectStyle();
  return new Promise((resolve) => {
    const back = document.createElement("div");
    back.className = "xp-back";

    const card = document.createElement("div");
    card.className = "xp-card";
    card.setAttribute("role", "dialog");
    card.setAttribute("aria-label", "Load a saved run");

    const h = document.createElement("div");
    h.className = "xp-h";
    h.textContent = "Load a saved run";
    card.appendChild(h);

    const list = document.createElement("div");
    list.className = "xp-list";
    if (!items.length) {
      const empty = document.createElement("div");
      empty.className = "xp-empty";
      empty.textContent = "Nothing saved yet. Run the sim, then use “Save to server”.";
      list.appendChild(empty);
    }
    for (const it of items) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "xp-item";
      const n = document.createElement("span");
      n.className = "xp-name";
      n.textContent = it.name;
      const m = document.createElement("span");
      m.className = "xp-meta";
      m.textContent = `seed ${it.seed} · ${when(it.updated_at)}`;
      b.appendChild(n);
      b.appendChild(m);
      b.addEventListener("click", () => close(it));
      list.appendChild(b);
    }
    card.appendChild(list);

    const foot = document.createElement("div");
    foot.className = "xp-foot";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "xp-cancel";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => close(null));
    foot.appendChild(cancel);
    card.appendChild(foot);

    back.appendChild(card);
    back.addEventListener("pointerdown", (e) => {
      if (e.target === back) close(null);
    });
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") close(null);
    };
    window.addEventListener("keydown", onKey);
    document.body.appendChild(back);

    function close(v: ExperimentSummary | null): void {
      window.removeEventListener("keydown", onKey);
      back.remove();
      resolve(v);
    }
  });
}

function when(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  return new Date(t).toLocaleDateString();
}

const STYLE_ID = "eco-experiments-style";
let styled = false;

function injectStyle(): void {
  if (styled) return;
  styled = true;
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
.xp-back {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.55);
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}
.xp-card {
  width: min(380px, 100%);
  max-height: min(70dvh, 560px);
  display: flex;
  flex-direction: column;
  background: var(--panel, #131811);
  border: 1px solid var(--line, #2a3327);
  border-radius: var(--r-md, 10px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.55);
  color: var(--ink, #dde5d4);
  overflow: hidden;
}
.xp-h {
  padding: 11px 13px;
  border-bottom: 1px solid var(--line, #2a3327);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--muted, #8a9580);
}
.xp-list { overflow-y: auto; padding: 6px; }
.xp-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 8px 10px;
  margin: 0 0 4px;
  border: 1px solid transparent;
  border-radius: var(--r-sm, 6px);
  background: var(--panel-2, #1a2017);
  color: inherit;
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
}
.xp-item:hover, .xp-item:focus-visible {
  border-color: var(--accent, #6fbf5c);
  outline: none;
}
.xp-name { font-size: 12.5px; font-weight: 500; }
.xp-meta {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  color: var(--muted, #8a9580);
}
.xp-empty { padding: 14px 10px; font-size: 11.5px; color: var(--muted, #8a9580); }
.xp-foot {
  display: flex;
  justify-content: flex-end;
  padding: 8px 10px;
  border-top: 1px solid var(--line, #2a3327);
}
.xp-cancel {
  padding: 5px 14px;
  border: 1px solid var(--line, #2a3327);
  border-radius: var(--r-sm, 6px);
  background: transparent;
  color: var(--muted, #8a9580);
  font-size: 11.5px;
  cursor: pointer;
  touch-action: manipulation;
}
.xp-cancel:hover { color: var(--ink, #dde5d4); border-color: var(--muted, #8a9580); }
`;
  document.head.appendChild(s);
}
